/* eslint-disable max-len */
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  NumberInput,
  SelectInput,
  SimpleForm,
  TextInput,
  useRecordContext,
  useRefresh,
  useUpdate
} from "react-admin";
import { useFormContext } from "react-hook-form";
import { ShowInDialogButton } from "@react-admin/ra-form-layout";
import * as Cognito from "providers/cognito";
import { useQuery } from "@apollo/client";
import { Avatar, Radio, TextField } from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { muiColors } from "themes/colors";
import { CustomButton } from "./CustomButton";
import ConfirmationModal from "./ConfirmationModal";
import * as Axios from "../providers/axios";
import {
  FailedOrderIcon,
  FullyPaidOrderIcon,
  MastercardIcon,
  TimeoutOrderIcon,
  VisaIcon
} from "../assets/Icons";
import { useParams } from "react-router-dom";
import * as User from "../providers/user";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { CARD_ELEMENT_OPTIONS } from "stripeConfig";
import { GET_USER_USING_ORDER } from "../gql";

const CardBrandIcon = ({ brand }: { brand: string }) => {
  if (brand === "mastercard") return <MastercardIcon />;

  return <VisaIcon />;
};

const applyCredits = (amount: number, credits: number) => {
  let creditsUsed = 0;
  let finalPayment = amount;
  let remainingCredits = credits;

  if (credits >= amount) {
    creditsUsed = amount;
    finalPayment = 0;
    remainingCredits = credits - amount;
  } else {
    creditsUsed = credits;
    finalPayment = amount - credits;
    remainingCredits = 0;
  }

  return { finalPayment, creditsUsed, remainingCredits };
};

const calculateTip = (
  amount: number,
  tipAmount: number,
  tippingType: string,
  fullAmount: number,
  artistsCount: number,
  userPaysFees?: boolean,
  paymentMethod?: string | null,
  tipAmountPerArtist?: Record<string, number>
): string => {
  let result = amount;
  if (tippingType === "FIXED_AMOUNT_PER_ARTIST" && tipAmountPerArtist) {
    const totalTipPerArtist = Object.values(tipAmountPerArtist).reduce(
      (acc, curr) => acc + (curr || 0),
      0
    );
    result += totalTipPerArtist;
    return result.toFixed(2);
  }
  switch (tippingType) {
    case "PERCENT_AMOUNT":
      result += ((tipAmount || 0) / 100) * fullAmount;
      break;
    case "FIXED_AMOUNT_PER_ARTIST":
      result += tipAmount * artistsCount;
      break;
    default:
      result += tipAmount;
  }
  if (userPaysFees && (paymentMethod === "STRIPE" || paymentMethod === "POS")) {
    result += result * 0.04;
  }
  return result.toFixed(2);
};

const sumTransactions = (
  transactions: any[],
  fullAmount: number,
  artistsCount: number
): number => {
  return transactions.reduce((acc, curr) => {
    return (
      acc +
      calculateTip(
        curr.amount,
        curr.total_tip_amount || 0,
        curr.tipping_type,
        fullAmount,
        artistsCount
      )
    );
  }, 0);
};

const CustomToolbar = ({
  loading,
  tipAmount,
  amount,
  setAmountRemaining,
  setAmount,
  statusModal,
  tippingType,
  paymentMethod,
  userPaysFees,
  tipAmountPerArtist
}: {
  loading: boolean;
  tipAmount: number;
  amount: number;
  setAmountRemaining: (amount: number) => void;
  setAmount: (amount: number) => void;
  statusModal: boolean;
  tippingType?: string;
  paymentMethod: string | null;
  userPaysFees: boolean;
  tipAmountPerArtist?: Record<string, number>;
}) => {
  const {
    amount_remaining,
    transactions,
    appointment,
    full_amount,
    tx_addons,
    tx_services
  } = useRecordContext();

  useEffect(() => {
    if (amount_remaining) {
      setAmountRemaining(amount_remaining);
      setAmount(amount_remaining);
    }
  }, [amount_remaining, setAmountRemaining, setAmount]);

  const uniqueArtistsCount = useMemo(() => {
    return new Set(
      [...tx_addons, ...tx_services].map((service: any) => service.artist_id)
    ).size;
  }, [tx_addons, tx_services]);

  const successfullTransactions =
    transactions?.filter((t: any) => t.status === "SUCCESS") || [];

  const totalSuccessfullAmount = sumTransactions(
    successfullTransactions,
    full_amount || appointment?.full_amount || 0,
    uniqueArtistsCount
  );

  return (
    <div className="flex justify-between items-center px-[16px] pb-[16px]">
      <div>
        {!statusModal ? (
          <>
            <div className="text-[12px] text-mono-secondaryTextColor font-['Inter'] font-[400]">
              Final amount (inc. fees):
            </div>
            <div className="text-[16px] text-mono-primaryTextColor font-['Inter'] font-[400]">
              $
              {calculateTip(
                amount,
                tipAmount || 0,
                tippingType || "",
                full_amount || appointment?.full_amount || 0,
                uniqueArtistsCount,
                userPaysFees,
                paymentMethod,
                tipAmountPerArtist
              )}
            </div>
          </>
        ) : (
          <div>
            $
            {totalSuccessfullAmount ||
              calculateTip(
                amount,
                tipAmount || 0,
                tippingType || "",
                full_amount || appointment?.full_amount || 0,
                uniqueArtistsCount,
                undefined,
                undefined,
                tipAmountPerArtist
              )}
          </div>
        )}
      </div>
      <CustomButton
        type="submit"
        variant="primary"
        cyId="charge-submit-button"
        text={statusModal ? "Got it" : "Charge"}
        disabled={loading}
      />
    </div>
  );
};

// Keeps the "Amount" input (form field `amount_remaining`) in sync with the
// reduced amount once credits are applied, since applying credits only updates
// the `amount` state and not the form field.
const CreditsAmountSync = ({
  amount,
  creditsApplied
}: {
  amount: number;
  creditsApplied: boolean;
}) => {
  const form = useFormContext();

  useEffect(() => {
    if (creditsApplied) {
      form.setValue("amount_remaining", Number(Number(amount).toFixed(2)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditsApplied, amount]);

  return null;
};

export default function OrderModal({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { id } = useParams<{ id: string }>();
  const { user } = User.useUser();
  const { api } = Axios.useAxios();
  const stripe = useStripe();
  const elements = useElements();
  const [edit] = useUpdate();
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [tippingType, setTippingType] = useState<string>("NO_TIP");
  const [cardDetails, setCardDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [tipAmount, setTipAmount] = useState<number | string>(0);
  const [tipAmountPerArtist, setTipAmountPerArtist] = useState<any>(null);
  const [amount, setAmount] = useState(0);
  const [amountRemaining, setAmountRemaining] = useState(0);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [isNewCardEmpty, setIsNewCardEmpty] = useState<boolean>(true);
  const [statusModal, setStatusModal] = useState(false);
  // const [isTippingModalOpen, setIsTippingModalOpen] = useState(false);
  const [webViewMessageData, setWebViewMessageData] = useState<any>(null);
  const [stripeErrorMsg, setStripeErrorMsg] = useState<string>("");
  const [credits, setCredits] = useState(0);
  const [creditsApplied, setCreditsApplied] = useState(false);
  const [isCreditsConfirmOpen, setIsCreditsConfirmOpen] = useState(false);
  const [customTip, setCustomTip] = useState<string>("");
  const tipInputRef = useRef<HTMLInputElement>(null);
  const refresh = useRefresh();
  const record = useRecordContext();
  const { user: cognitoUser } = Cognito.useCognito();

  const { data: orderData } = useQuery(GET_USER_USING_ORDER, {
    variables: {
      where: { id },
      refundWhere: {
        consumed: {
          equals: false
        }
      }
    },
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (orderData?.order?.user?.StripePaymentMethods) {
      const defaultCard = orderData?.order?.user?.StripePaymentMethods?.find(
        (card: any) => card.is_default
      );
      if (
        defaultCard &&
        setPaymentMethodId &&
        !paymentMethodId &&
        isNewCardEmpty
      ) {
        setPaymentMethodId(defaultCard.stripe_id);
      }
    }
  }, [
    orderData?.order?.user?.StripePaymentMethods,
    setPaymentMethodId,
    paymentMethodId,
    isNewCardEmpty
  ]);

  useEffect(() => {
    if (orderData?.order?.user?.Refund) {
      setCredits(
        orderData?.order?.user?.Refund.reduce(
          (acc: number, curr: any) => acc + curr.amount,
          0
        )
      );

      setCreditsApplied(false);
    }
  }, [orderData?.order?.user?.Refund]);

  function handleIsWebview() {
    if (globalThis.isReactNativeWebview === "true") {
      return true;
    }
    if (globalThis.isReactNativeWebview2 === "yes") {
      return true;
    }
    return false;
  }

  const isWebview = handleIsWebview();

  const resetStates = useCallback(
    (resetError: boolean) => {
      if (resetError) {
        setError(null);
        setStatusModal(false);
        setTipAmount(0);
        setAmount(0);
        setAmountRemaining(0);
      }
      setPaymentMethodId(null);
      setPaymentMethod(null);
      setTippingType("NO_TIP");
      if (webViewMessageData?.status) {
        setWebViewMessageData(null);
        refresh();
      }
    },
    [refresh, webViewMessageData?.status]
  );

  const handleResetAndClose = useCallback(() => {
    resetStates(true);
    setIsOpen(false);
  }, [resetStates, setIsOpen]);

  const handleMessage = useCallback(
    (event: any) => {
      if (
        event &&
        event?.data &&
        event?.origin !== window.location.origin &&
        event?.data?.action !== "card-element-link-report" &&
        event?.data?.action !== "card-element-link-load"
      ) {
        let messageObject: any = null;

        // In RN WebView, `event.data` is usually a JSON string.
        // In regular web (Stripe/CardElement), `event.data` is often an object.
        if (typeof event?.data === "string") {
          try {
            messageObject = JSON.parse(event.data);
          } catch (err) {}
        } else if (event?.data && typeof event.data === "object") {
          messageObject = event.data;
        }

        if (messageObject) {
          setWebViewMessageData(messageObject);
        }
        if (messageObject.status === "fail") {
          setError(messageObject?.message);
        }
        if (
          messageObject?.status === "success" ||
          messageObject.status === "timeout"
        ) {
          setError(null);
        }

        if (messageObject.action === "SEND_PAYMENT_INTENT_MESSAGE") {
          const paymentIntentId = messageObject.paymentIntentId;
          const orderId = messageObject.orderId;
          api
            .post(`/v1/payment-intent`, {
              paymentIntentId,
              orderId,
              sub: cognitoUser?.id
            })
            .then((response) => {
              refresh();
              handleResetAndClose();
            })
            .catch((error) => alert(error));
        }
      }
    },
    [setWebViewMessageData, api, cognitoUser?.id, refresh, handleResetAndClose]
  );

  useEffect(() => {
    // For Android
    document.addEventListener("message", handleMessage);
    // For IOS
    window.addEventListener("message", handleMessage);
    return () => {
      document.removeEventListener("message", handleMessage);
      window.removeEventListener("message", handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectPaymentMethodId = (id: string) => {
    elements?.getElement(CardElement)?.clear();
    setStripeErrorMsg("");
    setTimeout(() => {
      setPaymentMethodId(id);
    }, 0);
  };

  const handleCheckIn = (id: string) => {
    const updatedAppointment = {
      id,
      data: {
        checked_in_at: new Date().toISOString()
      }
    };

    edit("Appointment", updatedAppointment, {
      onSuccess: () => {},
      onError: () => {
        // eslint-disable-next-line no-console
        console.error(`Error occurred during checked_in_at action.`);
      }
    });
  };

  const onSubmit = async (data: any) => {
    if (loading) return;
    setLoading(true);
    try {
      if (record && !record.location.company.stripe_user_id) {
        throw new Error("Company is not connected to Stripe");
      }
      let stripe_id = paymentMethodId;
      if (
        data.amount_remaining === 0 &&
        tipAmount === 0 &&
        paymentMethod !== "POS"
      ) {
        setLoading(false);
        throw new Error("Please enter amount or tip amount");
      }
      if (paymentMethod === "STRIPE" && !paymentMethodId && isNewCardEmpty) {
        setStripeErrorMsg("Please select a credit card or enter a new one");
        setLoading(false);
        return;
      }
      if (
        paymentMethod === "STRIPE" &&
        !paymentMethodId &&
        stripe &&
        elements &&
        !isNewCardEmpty
      ) {
        const cardElement = elements.getElement(CardElement);

        if (cardElement) {
          const { error: errorRes, paymentMethod: paymentMethodRes } =
            await stripe.createPaymentMethod({
              type: "card",
              card: cardElement,
              billing_details: {
                name: `${orderData?.order?.user?.first_name} ${orderData?.order?.user?.last_name}`,
                email: orderData?.order?.user?.email,
                phone: orderData?.order?.user?.libphonenumber
              }
            });

          if (errorRes) {
            setStripeErrorMsg(errorRes?.message || "");
            setLoading(false);
            return;
          } else {
            try {
              const { data } = await api.post("/v1/payment-methods", {
                paymentMethod: {
                  id: paymentMethodRes?.id,
                  brand: paymentMethodRes?.card?.brand,
                  exp_month: paymentMethodRes?.card?.exp_month,
                  exp_year: paymentMethodRes?.card?.exp_year,
                  last4: paymentMethodRes?.card?.last4
                },
                is_default: false,
                user_id: orderData?.order?.user?.id,
                order_id: id,
                tip_amount: Number(tipAmount) ? Number(tipAmount) : 0,
                tipping_type: tippingType
              });
              stripe_id = data.stripe_id;
            } catch (e) {
              setStripeErrorMsg(e?.response?.data?.message || e?.message || "");
              setLoading(false);
              return;
            }
          }
        }
      }

      if (paymentMethod === "POS" && handleIsWebview()) {
        const action = {
          type: "stripe.terminal.payment",
          payload: {
            orderId: id,
            company_id: user.impersonate_company_id || user.company.id,
            customAmount: amount,
            accessToken: cognitoUser?.access_token
          }
        };
        // @ts-ignore
        window.ReactNativeWebView.postMessage(JSON.stringify(action));
      } else {
        const { data: paymentMethodData } = await api.post(
          `/v1/book-appointment/finish`,
          {
            payment_method: paymentMethod,
            tipping_type: tippingType,
            amount: Number(amount) ?? Number(data.amount_remaining),
            tip_amount:
              tippingType !== "PERCENT_AMOUNT"
                ? Number(tipAmount)
                  ? Number(tipAmount)
                  : 0
                : 0,
            description: paymentMethod === "GIFTCARD" ? data.description : null,
            tip_percentage:
              tippingType === "PERCENT_AMOUNT"
                ? Number(tipAmount)
                  ? Number(tipAmount)
                  : 0
                : 0,
            gift_card_code:
              paymentMethod === "GIFTCARD" ? data.gift_card_code : null,
            order_id: id,
            stripe_id
          }
        );
        setCardDetails(paymentMethodData.cardDetails);
      }
      setLoading(false);
      if (paymentMethod !== "POS") {
        setStatusModal(true);
        resetStates(false);
        refresh();
      }
      if (record?.appointmentId) {
        handleCheckIn(record.appointmentId);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setLoading(false);
      if (paymentMethod !== "POS") {
        setStatusModal(true);
        resetStates(false);
        refresh();
      }
    }
  };

  const handleAddCredits = async () => {
    setCreditsApplied(true);
    setLoading(true);

    let redirect = false;

    const { finalPayment, creditsUsed, remainingCredits } = applyCredits(
      amount,
      credits
    );

    setAmount(finalPayment);
    // Lower the max for the "Amount" input so it can't be edited back above
    // what's still owed after credits.
    setAmountRemaining(Number(finalPayment.toFixed(2)));
    setCredits(remainingCredits);

    if (finalPayment === 0) {
      setPaymentMethod("CREDITS");
      redirect = true;
    }

    try {
      await api.post("/v1/book-appointment/credits", {
        amount: finalPayment,
        credits_applied: creditsUsed,
        user_id: orderData?.order?.user?.id,
        company_id: user.impersonate_company_id || user.company.id,
        order_id: id
      });

      setLoading(false);
      if (redirect) {
        setStatusModal(true);
        resetStates(false);
        refresh();
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message);
      setLoading(false);
      if (redirect) {
        setStatusModal(true);
        resetStates(false);
        refresh();
      }
    }
  };

  const uniqueArtists = [...record?.tx_addons, ...record?.tx_services].reduce(
    (acc: any[], service: any) => {
      if (
        !acc.find((artist: any) => artist?.artist_id === service?.artist_id)
      ) {
        acc.push(service);
      }
      return acc;
    },
    []
  );

  return (
    <>
      {(statusModal || webViewMessageData?.status) && (
        // We are hiding dialog button because we are opening it with different button from different space
        <ShowInDialogButton
          title=" "
          label=" "
          fullWidth
          ButtonProps={{
            value: "Finish Order",
            variant: "contained",
            onClickCapture: () => setStatusModal(true),
            sx: { padding: 0, visibility: "hidden" }
          }}
          isOpen={statusModal || webViewMessageData?.status}
          onClose={handleResetAndClose}
          close={handleResetAndClose}
          sx={{
            "& .MuiDialog-paper": {
              backgroundColor: muiColors.paper,
              borderRadius: "8px",
              maxWidth: "400px"
            },
            "& #edit-dialog-title": {
              textAlign: "center",
              textTransform: "uppercase",
              fontFamily: "Inter",
              fontSize: "14px"
            },
            "& .RaFormDialogTitle-closeButton": {
              display: "none"
            }
          }}
        >
          <SimpleForm onSubmit={handleResetAndClose} toolbar={false}>
            <div className="w-full flex flex-col justify-center items-center pt-6">
              {error && <FailedOrderIcon />}
              {!error && webViewMessageData?.status !== "timeout" && (
                <FullyPaidOrderIcon />
              )}
              {!error && webViewMessageData?.status === "timeout" && (
                <TimeoutOrderIcon />
              )}
              {error && (
                <>
                  <p className="text-[24px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-4">
                    Payment Failed
                  </p>
                  <p className="text-[16px] font-['OptimaLTStd'] text-center font-[500] text-mono-primaryTextColor mt-4">
                    {error}
                  </p>
                </>
              )}
              {!error && (
                <p
                  data-cy="successful-payment"
                  className="text-[24px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-4"
                >
                  {typeof webViewMessageData?.message === "string"
                    ? webViewMessageData?.message
                    : "Successful Payment"}
                </p>
              )}
              {record?.transactions &&
                (() => {
                  const transactions = [...record.transactions].sort(
                    (a: any, b: any) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime()
                  );
                  const latestTransaction = transactions[0];

                  if (latestTransaction) {
                    const displayAmount = latestTransaction.amount;

                    if (cardDetails) {
                      return (
                        <div className="flex gap-[4px] text-[14px] text-mono-primaryTextColor font-[400] font-['Inter']">
                          <span>
                            ${webViewMessageData?.amount || displayAmount}
                          </span>
                          <span>
                            {webViewMessageData?.cardBrand
                              ? `• ${webViewMessageData?.cardBrand}`
                              : `• ${cardDetails?.brand}`}
                          </span>
                          <span>
                            {webViewMessageData?.lastFourDigits
                              ? `*${webViewMessageData?.lastFourDigits}`
                              : `*${cardDetails?.last4}`}
                          </span>
                        </div>
                      );
                    }
                  }
                })()}
              <div className="mt-4 mb-8">
                <CustomButton
                  cyId="charge-got-it-button"
                  text="Got it"
                  onClick={handleResetAndClose}
                />
              </div>
            </div>
          </SimpleForm>
        </ShowInDialogButton>
      )}
      {!statusModal && !webViewMessageData?.status && (
        // We are hiding dialog button because we are opening it with different button from different space
        <ShowInDialogButton
          title="Charge"
          label=" "
          fullWidth
          ButtonProps={{
            value: "Finish Order",
            variant: "contained",
            onClickCapture: () => setIsOpen(true),
            sx: { padding: 0, visibility: "hidden" }
          }}
          isOpen={isOpen}
          onClose={handleResetAndClose}
          close={handleResetAndClose}
          sx={{
            "& .MuiDialog-paper": {
              backgroundColor: muiColors.paper,
              borderRadius: "8px"
            },
            "& #edit-dialog-title": {
              textAlign: "center",
              textTransform: "uppercase",
              fontFamily: "Inter",
              fontSize: "14px"
            },
            "& .RaFormDialogTitle-closeButton": {
              display: "none"
            }
          }}
        >
          <SimpleForm
            onSubmit={onSubmit}
            resolver={yupResolver(validationSchema)}
            toolbar={
              <CustomToolbar
                loading={loading}
                tipAmount={Number(tipAmount) ? Number(tipAmount) : 0}
                amount={Number(amount)}
                setAmountRemaining={setAmountRemaining}
                setAmount={setAmount}
                statusModal={statusModal}
                tippingType={tippingType}
                paymentMethod={paymentMethod}
                userPaysFees={record.location.company.user_pays_fees}
                tipAmountPerArtist={tipAmountPerArtist}
              />
            }
            sx={{
              "& .MuiFilledInput-root": {
                borderRadius: "8px",
                border: "1px solid rgba(48, 47, 46, 0.06)"
              },
              "& .MuiFilledInput-input": {
                height: "24px",
                fontSize: "16px",
                borderRadius: "8px",
                fontFamily: "Inter",
                border: "none"
              },
              "& .MuiFilledInput-input:focus": {
                boxShadow: "none",
                borderRadius: "8px !important"
              },
              "& .MuiFilledInput-root:before": {
                border: "none !important"
              },
              "& .MuiFilledInput-root:after": {
                border: "1px solid #b8722a !important",
                top: "0px",
                borderRadius: "8px",
                transition: "none"
              },
              "& .MuiFilledInput-root.Mui-error:after": {
                border: "1px solid #EF5B1B !important"
              },
              "& .MuiFilledInput-root.Mui-disabled": {
                backgroundColor: "rgba(48, 47, 46, 0.06)"
              },
              "& .MuiTextField-root": {
                marginTop: "0px",
                marginBottom: "0px"
              },
              "& .MuiInputLabel-root": {
                fontSize: "16px",
                color: "rgba(48, 47, 46, 0.6)"
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#b8722a"
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                marginTop: "2px",
                marginBottom: "2px",
                lineHeight: "14px"
              },
              "[type='number']:focus": {
                boxShadow: "none"
              }
            }}
          >
            <CreditsAmountSync
              amount={Number(amount)}
              creditsApplied={creditsApplied}
            />
            {error && <p style={{ color: "red" }}>{error}</p>}
            <SelectInput
              source="payment_method"
              data-cy="payment-method-select"
              choices={
                isWebview
                  ? [
                      { id: "STRIPE", name: "Credit Card" },
                      { id: "CASH", name: "Cash" },
                      { id: "POS", name: "POS" },
                      { id: "GIFTCARD", name: "Gift Card" }
                    ]
                  : [
                      { id: "STRIPE", name: "Credit Card" },
                      { id: "CASH", name: "Cash" },
                      { id: "GIFTCARD", name: "Gift Card" }
                    ]
              }
              fullWidth
              disabled={creditsApplied && amount === 0}
              onChange={(e) => {
                setPaymentMethod(e.target.value);
                setPaymentMethodId(null);
                elements?.getElement(CardElement)?.clear();
                setError(null);
              }}
              sx={{
                "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                  {
                    borderBottomLeftRadius: "0px",
                    borderBottomRightRadius: "0px"
                  },
                "& .MuiFormHelperText-root": {
                  display: "none"
                }
              }}
            />
            {/*{paymentMethod !== "POS" && (*/}
            <NumberInput
              source="amount_remaining"
              label="Amount"
              min={0}
              max={Number(amountRemaining)}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value > Number(amountRemaining)) {
                  e.target.value = amountRemaining;
                  setAmount(Number(amountRemaining));
                } else {
                  setAmount(value);
                }
              }}
              fullWidth
              sx={{
                "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                  {
                    borderBottomLeftRadius:
                      paymentMethod === "GIFTCARD" ? "0px" : "8px",
                    borderBottomRightRadius:
                      paymentMethod === "GIFTCARD" ? "0px" : "8px",
                    borderTopLeftRadius: "0px",
                    borderTopRightRadius: "0px"
                  },
                "& .MuiFormHelperText-root": {
                  display: "none"
                }
              }}
            />
            {(credits > 0 || creditsApplied) && (
              <div className="flex w-[100%] justify-between items-center mt-4">
                <div className="text-[16px] text-mono-primaryTextColor font-['Inter'] font-[400]">
                  User Credits: {credits}
                </div>
                <CustomButton
                  variant="tertiary"
                  text={creditsApplied ? "Credits Applied" : "Add Credits"}
                  disabled={creditsApplied}
                  onClick={() => setIsCreditsConfirmOpen(true)}
                />
              </div>
            )}
            <ConfirmationModal
              open={isCreditsConfirmOpen}
              text="Are you sure you want to apply credits? They will be applied immediately and this can't be undone."
              buttonText="Apply Credits"
              buttonBg={muiColors.primary}
              // centerAlign
              handleReject={() => setIsCreditsConfirmOpen(false)}
              rejectText="Cancel"
              onClose={() => setIsCreditsConfirmOpen(false)}
              handleConfirm={() => {
                setIsCreditsConfirmOpen(false);
                handleAddCredits();
              }}
            />
            {paymentMethod === "GIFTCARD" && (
              <>
                <TextInput
                  source="gift_card_code"
                  label="Code"
                  fullWidth
                  sx={{
                    "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                      {
                        borderRadius: "0px"
                      },
                    "& .MuiFormHelperText-root": {
                      display: "none"
                    }
                  }}
                />
                <TextInput
                  source="description"
                  label="Description"
                  fullWidth
                  sx={{
                    "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                      {
                        borderTopLeftRadius: "0px",
                        borderTopRightRadius: "0px"
                      },
                    "& .MuiFormHelperText-root": {
                      display: "none"
                    }
                  }}
                />
              </>
            )}
            <div className="h-[16px]" />
            {paymentMethod !== "POS" && (
              <SelectInput
                source="tipping_type"
                data-cy="tip-type-select"
                choices={[
                  { id: "FIXED_AMOUNT", name: "Tip" }
                  // { id: "PERCENT_AMOUNT", name: "Percentage Amount" },
                  // {
                  //   id: "FIXED_AMOUNT_PER_ARTIST",
                  //   name: "Fixed Amount Per Artist"
                  // }
                ]}
                defaultValue={"NO_TIP"}
                emptyValue={"NO_TIP"}
                emptyText={"No Tip"}
                fullWidth
                onChange={(e) => {
                  setTippingType(e.target.value);
                  if (!!customTip) {
                    setTipAmount(0);
                  }
                  setCustomTip("");
                  setTipAmountPerArtist(null);
                  if (e.target.value === "NO_TIP") {
                    setTipAmount(0);
                  }
                  // if (e.target.value === "FIXED_AMOUNT") {
                  //   setIsTippingModalOpen(true);
                  // }
                }}
                sx={{
                  "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                    {
                      borderBottomLeftRadius:
                        tippingType === "PERCENT_AMOUNT" ? "0px" : "8px",
                      borderBottomRightRadius:
                        tippingType === "PERCENT_AMOUNT" ? "0px" : "8px"
                    },
                  "& .MuiFormHelperText-root": {
                    display: "none"
                  }
                }}
              />
            )}
            {tippingType === "FIXED_AMOUNT_PER_ARTIST" && (
              <div className="w-full flex flex-col gap-4 mt-4">
                {uniqueArtists.map((item: any) => (
                  <div key={item?.artist_id} className="w-full">
                    <div
                      className="flex h-12 items-center gap-2 pl-3"
                      style={{
                        border: "1px solid rgba(48, 47, 46, 0.1)",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px"
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "rgba(238, 234, 228, 0.9)",
                          color: "rgba(48, 47, 46, 0.95)",
                          width: "32px",
                          height: "32px",
                          fontFamily: "Inter",
                          fontSize: "18px"
                          // "@media screen and (max-width:800px)": {
                          //   width: "24px",
                          //   height: "24px"
                          // }
                        }}
                        alt={item?.artist?.user?.first_name}
                        src={
                          item?.artist?.cover_photo_url ||
                          item?.artist?.user?.photo_url ||
                          "monogram"
                        }
                      />
                      <div className="text-[16px] leading-6 font-[400]">
                        {item?.artist?.user?.first_name}{" "}
                        {item?.artist?.user?.last_name}
                      </div>
                    </div>
                    <div className="w-full relative">
                      <TextField
                        variant="filled"
                        name={"tip_amount" + item?.artist_id}
                        type="number"
                        label="Tip Amount"
                        inputMode="numeric"
                        InputProps={{
                          inputProps: {
                            min: 0,
                            nullable: "true",
                            inputMode: "numeric",
                            // pattern: "[0-9]*",
                            "data-cy": "tip-amount-input"
                          }
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                        value={tipAmountPerArtist?.[item?.artist_id]}
                        defaultValue={0}
                        onFocus={(e) => {
                          if (e.target.value === "0") {
                            setTipAmountPerArtist((prev: any) => ({
                              ...prev,
                              [item?.artist_id]: ""
                            }));
                          }
                        }}
                        onBlur={(e) => {
                          if (e.target.value === "") {
                            setTipAmountPerArtist((prev: any) => ({
                              ...prev,
                              [item?.artist_id]: 0
                            }));
                          }
                        }}
                        onChange={(e) => {
                          const value = +e.target.value;
                          if (value < 0) {
                            setTipAmountPerArtist((prev: any) => ({
                              ...prev,
                              [item?.artist_id]: 0
                            }));
                          } else {
                            if (e.target.value === "") {
                              setTipAmountPerArtist((prev: any) => ({
                                ...prev,
                                [item?.artist_id]: ""
                              }));
                            } else {
                              setTipAmountPerArtist((prev: any) => ({
                                ...prev,
                                [item?.artist_id]: value
                              }));
                            }
                          }
                        }}
                        fullWidth
                        sx={{
                          "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                            {
                              borderTopLeftRadius: "0px",
                              borderTopRightRadius: "0px",
                              paddingLeft: "12px"
                            },
                          "& .MuiFormHelperText-root": {
                            display: "none"
                          }
                        }}
                      />
                      <div className="absolute top-[21px] left-[12px]">$</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(tippingType === "FIXED_AMOUNT" ||
              (tippingType === "PERCENT_AMOUNT" && !!customTip)) && (
              <div className="w-[100%] flex flex-col gap-4 mt-4">
                <button
                  className="flex h-12 justify-center items-center border-mono-primary rounded-[8px] font-[500] text-[16px]"
                  style={{
                    backgroundColor:
                      customTip === "20%"
                        ? muiColors.primary
                        : muiColors.button.contrast,
                    color:
                      customTip === "20%"
                        ? muiColors.button.contrast
                        : muiColors.primary
                  }}
                  type="button"
                  onClick={() => {
                    setTippingType("PERCENT_AMOUNT");
                    setCustomTip("20%");
                    setTipAmount(20);
                  }}
                >
                  20% ($
                  {calculateTip(
                    0,
                    20,
                    "PERCENT_AMOUNT",
                    record?.full_amount || 0,
                    1,
                    false,
                    paymentMethod,
                    tipAmountPerArtist
                  )}
                  )
                </button>
                <button
                  className="flex h-12 justify-center items-center border-mono-primary rounded-[8px] font-[500] text-[16px]"
                  style={{
                    backgroundColor:
                      customTip === "25%"
                        ? muiColors.primary
                        : muiColors.button.contrast,
                    color:
                      customTip === "25%"
                        ? muiColors.button.contrast
                        : muiColors.primary
                  }}
                  type="button"
                  onClick={() => {
                    setTippingType("PERCENT_AMOUNT");
                    setCustomTip("25%");
                    setTipAmount(25);
                  }}
                >
                  25% ($
                  {calculateTip(
                    0,
                    25,
                    "PERCENT_AMOUNT",
                    record?.full_amount || 0,
                    1,
                    false,
                    paymentMethod,
                    tipAmountPerArtist
                  )}
                  )
                </button>
                <button
                  className="flex h-12 justify-center items-center border-mono-primary rounded-[8px] font-[500] text-[16px]"
                  style={{
                    backgroundColor:
                      customTip === "30%"
                        ? muiColors.primary
                        : muiColors.button.contrast,
                    color:
                      customTip === "30%"
                        ? muiColors.button.contrast
                        : muiColors.primary
                  }}
                  type="button"
                  onClick={() => {
                    setTippingType("PERCENT_AMOUNT");
                    setCustomTip("30%");
                    setTipAmount(30);
                  }}
                >
                  30% ($
                  {calculateTip(
                    0,
                    30,
                    "PERCENT_AMOUNT",
                    record?.full_amount || 0,
                    1,
                    false,
                    paymentMethod,
                    tipAmountPerArtist
                  )}
                  )
                </button>
                {/* <div className="w-full"> */}
                <button
                  className="flex h-12 justify-center items-center border-mono-primary rounded-[8px] font-[500] text-[16px]"
                  style={{
                    backgroundColor:
                      customTip === "OTHER"
                        ? muiColors.primary
                        : muiColors.button.contrast,
                    color:
                      customTip === "OTHER"
                        ? muiColors.button.contrast
                        : muiColors.primary,
                    borderBottomLeftRadius:
                      customTip === "OTHER" ? "0px" : "8px",
                    borderBottomRightRadius:
                      customTip === "OTHER" ? "0px" : "8px"
                  }}
                  type="button"
                  onClick={() => {
                    setTippingType("FIXED_AMOUNT");
                    setTipAmount(0);
                    setCustomTip("OTHER");
                    // if (tipInputRef?.current) {
                    setTimeout(() => {
                      tipInputRef?.current?.focus();
                    }, 300);
                    // }
                  }}
                >
                  Other
                </button>
                {/* </div> */}
              </div>
            )}
            {((tippingType !== "NO_TIP" &&
              tippingType !== "FIXED_AMOUNT" &&
              tippingType !== "FIXED_AMOUNT_PER_ARTIST" &&
              !customTip) ||
              customTip === "OTHER") && (
              <div className="w-full relative">
                <TextField
                  variant="filled"
                  name="tip_amount"
                  type="number"
                  label="Tip Amount"
                  inputMode="numeric"
                  InputProps={{
                    inputProps: {
                      min: 0,
                      nullable: "true",
                      inputMode: "numeric",
                      "data-cy": "tip-amount-input"
                    },
                    inputRef: tipInputRef
                  }}
                  value={tipAmount.toString()}
                  onFocus={(e) => {
                    if (e.target.value === "0") {
                      setTipAmount("");
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      setTipAmount(0);
                    }
                  }}
                  onChange={(e) => {
                    const value = +e.target.value;
                    if (value < 0) {
                      setTipAmount(0);
                    } else {
                      if (e.target.value === "") {
                        setTipAmount("");
                      } else {
                        setTipAmount(value);
                      }
                    }
                  }}
                  fullWidth
                  sx={{
                    "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                      {
                        borderTopLeftRadius: "0px",
                        borderTopRightRadius: "0px",
                        borderTopWidth: customTip === "OTHER" ? "0px" : "1px",
                        // paddingLeft: customTip === "OTHER" ? "12px" : "6px"
                        paddingLeft: "18px"
                      },
                    "& .MuiFormHelperText-root": {
                      display: "none"
                    }
                  }}
                />
                <IconButton
                  tabIndex={-1}
                  sx={{
                    width: "32px",
                    height: "32px",
                    position: "absolute",
                    top: "18px",
                    left: "3px",
                    backgroundColor: "rgba(184, 114, 42, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(184, 114, 42, 0.3)"
                    }
                  }}
                  onClick={() => {
                    if (tippingType === "FIXED_AMOUNT") {
                      setTippingType("PERCENT_AMOUNT");
                    } else {
                      setTippingType("FIXED_AMOUNT");
                    }
                  }}
                >
                  <span className="text-[16px] font-['Inter'] text-mono-black font-[500]">
                    {tippingType === "FIXED_AMOUNT" ? "$" : "%"}
                  </span>
                </IconButton>
              </div>
            )}
            {paymentMethod === "STRIPE" && (
              <div className="w-full">
                {orderData?.order?.user?.StripePaymentMethods?.length > 0 && (
                  <div className="text-[14px] font-['Inter'] font-[500] my-4">
                    Client's credit cards:
                  </div>
                )}
                <div>
                  {orderData?.order?.user?.StripePaymentMethods?.map(
                    (card: any) => (
                      <div
                        key={card.id}
                        onClick={() => selectPaymentMethodId(card.stripe_id)}
                        data-cy="credit-card"
                        aria-hidden
                        className="w-full flex justify-between items-center px-4 py-[6px] cursor-pointer
                      border-[rgba(48,47,46,0.15)] border-b first:border-t"
                      >
                        <div className="flex items-center gap-[16px]">
                          {card?.brand && <CardBrandIcon brand={card?.brand} />}
                          <div>
                            <div className="text-[14px] font-['Inter'] font-[400] text-mono-primaryTextColor capitalize">
                              {card?.brand} * {card?.last4}{" "}
                              {card?.is_default ? "(default)" : ""}
                            </div>
                            <div className="text-[14px] font-['Inter'] font-[400] text-mono-secondaryTextColor">
                              {card?.exp_month < 10
                                ? `0${card?.exp_month}`
                                : card?.exp_month}
                              /{card?.exp_year?.toString()?.slice(2, 4)}
                            </div>
                          </div>
                        </div>
                        <Radio
                          checked={paymentMethodId === card.stripe_id}
                          sx={{
                            "&.Mui-checked": {
                              color: muiColors.primary
                            }
                          }}
                        />
                      </div>
                    )
                  )}
                </div>
                <br />
                <div className="text-[14px] font-['Inter'] font-[500] mb-2">
                  New credit card:
                </div>
                <CardElement
                  onChange={(e) => {
                    if (e?.complete && !e?.error) {
                      setStripeErrorMsg("");
                    }
                    if (e?.empty) {
                      setIsNewCardEmpty(true);
                    } else {
                      setIsNewCardEmpty(false);
                      if (paymentMethod) {
                        setPaymentMethodId(null);
                      }
                    }
                  }}
                  options={CARD_ELEMENT_OPTIONS}
                />
                {stripeErrorMsg && (
                  <span className="text-error text-[12px]">
                    {stripeErrorMsg}
                  </span>
                )}
              </div>
            )}
            <IconButton
              sx={{
                position: "absolute",
                top: "8px",
                right: "8px"
              }}
              onClick={handleResetAndClose}
            >
              <CloseIcon />
            </IconButton>
          </SimpleForm>
        </ShowInDialogButton>
      )}
      {/* {isTippingModalOpen && (
        <ShowInDialogButton
          title=" "
          label=" "
          fullWidth
          ButtonProps={{
            value: translate("order.finish_order"),
            variant: "contained",
            onClickCapture: () => setStatusModal(true),
            sx: { padding: 0, visibility: "hidden" }
          }}
          isOpen={statusModal || webViewMessageData?.status}
          onClose={handleResetAndClose}
          close={handleResetAndClose}
          sx={{
            "& .MuiDialog-paper": {
              backgroundColor: muiColors.paper,
              borderRadius: "8px",
              maxWidth: "400px"
            },
            "& #edit-dialog-title": {
              textAlign: "center",
              textTransform: "uppercase",
              fontFamily: "Inter",
              fontSize: "14px"
            },
            "& .RaFormDialogTitle-closeButton": {
              display: "none"
            }
          }}
        >
          <SimpleForm
            onSubmit={handleResetAndClose}
            toolbar={false}
            sx={{
              "& .MuiFilledInput-root": {
                borderRadius: "8px",
                border: "1px solid rgba(48, 47, 46, 0.06)"
              },
              "& .MuiFilledInput-input": {
                height: "24px",
                fontSize: "16px",
                borderRadius: "8px",
                fontFamily: "Inter",
                border: "none"
              },
              "& .MuiFilledInput-input:focus": {
                boxShadow: "none",
                borderRadius: "8px !important"
              },
              "& .MuiFilledInput-root:before": {
                border: "none !important"
              },
              "& .MuiFilledInput-root:after": {
                border: "1px solid #b8722a !important",
                top: "0px",
                borderRadius: "8px",
                transition: "none"
              },
              "& .MuiFilledInput-root.Mui-error:after": {
                border: "1px solid #EF5B1B !important"
              },
              "& .MuiFilledInput-root.Mui-disabled": {
                backgroundColor: "rgba(48, 47, 46, 0.06)"
              },
              "& .MuiTextField-root": {
                marginTop: "0px",
                marginBottom: "0px"
              },
              "& .MuiInputLabel-root": {
                fontSize: "16px",
                color: "rgba(48, 47, 46, 0.6)"
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#b8722a"
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                marginTop: "2px",
                marginBottom: "2px",
                lineHeight: "14px"
              },
              "[type='number']:focus": {
                boxShadow: "none"
              }
            }}
          >
            <div className="w-full">
              <div className="w-[100%] flex flex-col gap-4 mt-4">
                <button
                  className="flex h-12 justify-center items-center border-mono-primary rounded-[8px] font-[500] text-[16px]"
                  style={{
                    backgroundColor:
                      customTip === "20%"
                        ? muiColors.primary
                        : muiColors.button.contrast,
                    color:
                      customTip === "20%"
                        ? muiColors.button.contrast
                        : muiColors.primary
                  }}
                  type="button"
                  onClick={() => {
                    setTippingType("PERCENT_AMOUNT");
                    setCustomTip("20%");
                    setTipAmount(20);
                  }}
                >
                  20% ($
                  {calculateTip(
                    amount,
                    20,
                    "PERCENT_AMOUNT",
                    amount || record?.full_amount || 0,
                    1,
                    record?.location?.company.user_pays_fees,
                    paymentMethod,
                    tipAmountPerArtist
                  )}
                  )
                </button>
                <button
                  className="flex h-12 justify-center items-center border-mono-primary rounded-[8px] font-[500] text-[16px]"
                  style={{
                    backgroundColor:
                      customTip === "25%"
                        ? muiColors.primary
                        : muiColors.button.contrast,
                    color:
                      customTip === "25%"
                        ? muiColors.button.contrast
                        : muiColors.primary
                  }}
                  type="button"
                  onClick={() => {
                    setTippingType("PERCENT_AMOUNT");
                    setCustomTip("25%");
                    setTipAmount(25);
                  }}
                >
                  25% ($
                  {calculateTip(
                    amount,
                    25,
                    "PERCENT_AMOUNT",
                    amount || record?.full_amount || 0,
                    1,
                    record?.location?.company.user_pays_fees,
                    paymentMethod,
                    tipAmountPerArtist
                  )}
                  )
                </button>
                <button
                  className="flex h-12 justify-center items-center border-mono-primary rounded-[8px] font-[500] text-[16px]"
                  style={{
                    backgroundColor:
                      customTip === "30%"
                        ? muiColors.primary
                        : muiColors.button.contrast,
                    color:
                      customTip === "30%"
                        ? muiColors.button.contrast
                        : muiColors.primary
                  }}
                  type="button"
                  onClick={() => {
                    setTippingType("PERCENT_AMOUNT");
                    setCustomTip("30%");
                    setTipAmount(30);
                  }}
                >
                  30% ($
                  {calculateTip(
                    amount,
                    30,
                    "PERCENT_AMOUNT",
                    amount || record?.full_amount || 0,
                    1,
                    record?.location?.company.user_pays_fees,
                    paymentMethod,
                    tipAmountPerArtist
                  )}
                  )
                </button>
                <button
                  className="flex h-12 justify-center items-center border-mono-primary rounded-[8px] font-[500] text-[16px]"
                  style={{
                    backgroundColor:
                      customTip === "OTHER"
                        ? muiColors.primary
                        : muiColors.button.contrast,
                    color:
                      customTip === "OTHER"
                        ? muiColors.button.contrast
                        : muiColors.primary,
                    borderBottomLeftRadius:
                      customTip === "OTHER" ? "0px" : "8px",
                    borderBottomRightRadius:
                      customTip === "OTHER" ? "0px" : "8px"
                  }}
                  type="button"
                  onClick={() => {
                    setTippingType("FIXED_AMOUNT");
                    setTipAmount(0);
                    setCustomTip("OTHER");

                    setTimeout(() => {
                      tipInputRef?.current?.focus();
                    }, 300);
                  }}
                >
                  Other
                </button>
              </div>
              {((tippingType !== "NO_TIP" &&
                tippingType !== "FIXED_AMOUNT" &&
                tippingType !== "FIXED_AMOUNT_PER_ARTIST" &&
                !customTip) ||
                customTip === "OTHER") && (
                <div className="w-full relative">
                  <TextField
                    variant="filled"
                    name="tip_amount"
                    type="number"
                    label={translate("order.tip_amount")}
                    inputMode="numeric"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        nullable: "true",
                        inputMode: "numeric",
                        "data-cy": "tip-amount-input"
                      },
                      inputRef: tipInputRef
                    }}
                    value={tipAmount.toString()}
                    onFocus={(e) => {
                      if (e.target.value === "0") {
                        setTipAmount("");
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === "") {
                        setTipAmount(0);
                      }
                    }}
                    onChange={(e) => {
                      const value = +e.target.value;
                      if (value < 0) {
                        setTipAmount(0);
                      } else {
                        if (e.target.value === "") {
                          setTipAmount("");
                        } else {
                          setTipAmount(value);
                        }
                      }
                    }}
                    fullWidth
                    sx={{
                      "& .MuiFilledInput-root, .MuiFilledInput-input, .MuiFilledInput-input:focus, .MuiFilledInput-root:after":
                        {
                          borderTopLeftRadius: "0px",
                          borderTopRightRadius: "0px",
                          borderTopWidth: customTip === "OTHER" ? "0px" : "1px",
                          paddingLeft: "14px"
                        },
                      "& .MuiFormHelperText-root": {
                        display: "none"
                      }
                    }}
                  />
                  <div className="absolute top-[20px] left-[12px]">
                    {customTip === "OTHER" ? "$" : "%"}
                  </div>
                </div>
              )}
              <div className="mt-4 mb-8">
                <CustomButton
                  cyId="charge-got-it-button"
                  text="Got it"
                  onClick={handleResetAndClose}
                />
              </div>
            </div>
          </SimpleForm>
        </ShowInDialogButton>
      )} */}
    </>
  );
}

const validationSchema = yup.object().shape({
  payment_method: yup
    .string()
    .oneOf(["STRIPE", "CASH", "POS", "GIFTCARD"])
    .required("Required"),
  tipping_type: yup
    .string()
    .oneOf([
      "FIXED_AMOUNT",
      "PERCENT_AMOUNT",
      "FIXED_AMOUNT_PER_ARTIST",
      "NO_TIP"
    ])
    .required("Required")
});
